import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const STATUS_LABEL = {
  new:       { label: 'Nuevo',      color: '#e91e63', bg: '#fde8ef' },
  contacted: { label: 'Contactado', color: '#f39c12', bg: '#fdf3e2' },
  booked:    { label: 'Reservado',  color: '#27ae60', bg: '#e8f8ee' },
  declined:  { label: 'Rechazado',  color: '#80868b',    bg: '#f0f0f0' },
}

export default function Leads() {
  const [leads, setLeads] = useState([])
  const [activity, setActivity] = useState({}) // { leadId: [ {id, note, created_at}, ... ] }
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [converting, setConverting] = useState(null)
  const [expanded, setExpanded] = useState(null) // leadId currently showing its log
  const [draftNote, setDraftNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  useEffect(() => { fetchLeads() }, [])

  async function fetchLeads() {
    setLoading(true)
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false })
    if (!error) setLeads(data || [])

    const { data: activityData, error: activityError } = await supabase
      .from('lead_activity')
      .select('*')
      .order('created_at', { ascending: false })
    if (!activityError && activityData) {
      const grouped = {}
      activityData.forEach(a => {
        if (!grouped[a.lead_id]) grouped[a.lead_id] = []
        grouped[a.lead_id].push(a)
      })
      setActivity(grouped)
    }
    setLoading(false)
  }

  function toggleExpanded(leadId) {
    setExpanded(expanded === leadId ? null : leadId)
    setDraftNote('')
  }

  async function addNote(leadId) {
    const note = draftNote.trim()
    if (!note) return
    setSavingNote(true)
    const { data, error } = await supabase
      .from('lead_activity')
      .insert({ lead_id: leadId, note })
      .select()
    if (!error && data) {
      setActivity(a => ({
        ...a,
        [leadId]: [data[0], ...(a[leadId] || [])],
      }))
      setDraftNote('')
    }
    setSavingNote(false)
  }

  async function updateStatus(id, status) {
    setLeads(ls => ls.map(l => l.id === id ? { ...l, status } : l))
    await supabase.from('leads').update({ status }).eq('id', id)
  }

  async function convertToClient(lead) {
    setConverting(lead.id)
    const { error } = await supabase.from('clients').insert({
      name: lead.name,
      email: lead.email || '',
      address: lead.address || '',
      city: lead.city || '',
      notes: [
        lead.notes,
        lead.job_type ? `Tipo de trabajo inicial: ${lead.job_type}` : '',
        lead.addons ? `Add-ons solicitados: ${lead.addons}` : '',
        lead.estimated_price ? `Rango estimado original: ${lead.estimated_price}` : '',
      ].filter(Boolean).join('\n'),
    })
    if (!error) {
      await updateStatus(lead.id, 'booked')
    }
    setConverting(null)
  }

  const filtered = filter === 'all' ? leads : leads.filter(l => l.status === filter)
  const counts = leads.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1
    return acc
  }, {})

  if (loading) return <div style={s.loading}>🌸 Cargando leads...</div>

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h2 style={s.h2}>📋 Leads — Cotizaciones</h2>
        <div style={s.filterRow}>
          {['all', 'new', 'contacted', 'booked', 'declined'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{ ...s.filterBtn, ...(filter === f ? s.filterBtnActive : {}) }}
            >
              {f === 'all' ? 'Todos' : STATUS_LABEL[f].label}
              {' '}
              {f === 'all' ? `(${leads.length})` : `(${counts[f] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div style={s.empty}>No hay leads en esta categoría todavía.</div>
      )}

      <div style={s.list}>
        {filtered.map(lead => {
          const st = STATUS_LABEL[lead.status] || STATUS_LABEL.new
          return (
            <div key={lead.id} style={s.card}>
              <div style={s.cardTop}>
                <div>
                  <div style={s.name}>{lead.name}</div>
                  <div style={s.sub}>{lead.address}{lead.city ? `, ${lead.city}` : ''}</div>
                </div>
                <span style={{ ...s.badge, color: st.color, background: st.bg }}>{st.label}</span>
              </div>

              <div style={s.detailsGrid}>
                {lead.phone && <div style={s.detail}><b>Tel:</b> {lead.phone}</div>}
                {lead.email && <div style={s.detail}><b>Email:</b> {lead.email}</div>}
                {lead.home_type && <div style={s.detail}><b>Tipo:</b> {lead.home_type === 'airbnb' ? 'Airbnb / Turnover' : 'Estándar'}</div>}
                {lead.job_type && <div style={s.detail}><b>Servicio:</b> {lead.job_type}</div>}
                {lead.sqft && <div style={s.detail}><b>Sqft:</b> {lead.sqft}</div>}
                {(lead.bedrooms || lead.bathrooms) && (
                  <div style={s.detail}><b>Recámaras/Baños:</b> {lead.bedrooms || '–'} / {lead.bathrooms || '–'}</div>
                )}
                {lead.addons && <div style={s.detail}><b>Add-ons:</b> {lead.addons}</div>}
                {lead.preferred_date && <div style={s.detail}><b>Fecha preferida:</b> {lead.preferred_date}</div>}
                {lead.estimated_price && (
                  <div style={{ ...s.detail, ...s.price }}>💲 Estimado: {lead.estimated_price}</div>
                )}
              </div>

              {lead.notes && <div style={s.notes}>📝 {lead.notes}</div>}

              <div style={s.actions}>
                <select
                  value={lead.status}
                  onChange={e => updateStatus(lead.id, e.target.value)}
                  style={s.statusSelect}
                >
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                {lead.status !== 'booked' && (
                  <button
                    style={s.convertBtn}
                    disabled={converting === lead.id}
                    onClick={() => convertToClient(lead)}
                  >
                    {converting === lead.id ? 'Convirtiendo...' : '➕ Convertir a cliente'}
                  </button>
                )}
              </div>

              <div style={s.timestamp}>
                Recibido: {new Date(lead.created_at).toLocaleString('es-US')}
              </div>

              <button
                style={s.logToggle}
                onClick={() => toggleExpanded(lead.id)}
              >
                {expanded === lead.id ? '▲ Ocultar registro de contacto' : `▼ Registro de contacto (${(activity[lead.id] || []).length})`}
              </button>

              {expanded === lead.id && (
                <div style={s.logBox}>
                  <div style={s.logAdd}>
                    <textarea
                      style={s.logTextarea}
                      rows={2}
                      placeholder="Ej: Llamé, dejó mensaje. O: Confirmó cita para el jueves 2pm."
                      value={draftNote}
                      onChange={e => setDraftNote(e.target.value)}
                    />
                    <button
                      style={s.logSaveBtn}
                      disabled={savingNote || !draftNote.trim()}
                      onClick={() => addNote(lead.id)}
                    >
                      {savingNote ? '...' : 'Guardar'}
                    </button>
                  </div>

                  {(activity[lead.id] || []).length === 0 ? (
                    <div style={s.logEmpty}>Sin notas todavía.</div>
                  ) : (
                    <div style={s.logList}>
                      {(activity[lead.id] || []).map(a => (
                        <div key={a.id} style={s.logEntry}>
                          <div style={s.logEntryDate}>
                            {new Date(a.created_at).toLocaleString('es-US')}
                          </div>
                          <div style={s.logEntryText}>{a.note}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const s = {
  wrap: { padding: '20px', maxWidth: 900, margin: '0 auto' },
  loading: { padding: 40, textAlign: 'center', fontSize: 16, color: '#80868b' },
  header: { marginBottom: 16 },
  h2: { fontSize: 20, color: '#333', marginBottom: 12 },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterBtn: {
    padding: '6px 14px', borderRadius: 20, border: '1.5px solid #e3e5e8',
    background: '#fff', fontSize: 13, cursor: 'pointer', color: '#5f6368',
  },
  filterBtnActive: { background: '#e91e63', borderColor: '#e91e63', color: '#fff' },
  empty: { padding: 30, textAlign: 'center', color: '#aaa', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 14 },
  card: {
    background: '#fff', borderRadius: 14, padding: 16,
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f5ebe4',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  name: { fontSize: 16, fontWeight: 700, color: '#333' },
  sub: { fontSize: 13, color: '#80868b', marginTop: 2 },
  badge: { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 12, whiteSpace: 'nowrap' },
  detailsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px',
    fontSize: 13, color: '#5f6368', marginBottom: 8,
  },
  detail: {},
  price: { fontWeight: 700, color: '#e91e63', gridColumn: '1 / -1' },
  notes: { fontSize: 13, color: '#777', background: '#f8f9fa', padding: 8, borderRadius: 8, marginBottom: 10 },
  actions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  statusSelect: {
    padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e3e5e8',
    fontSize: 13, background: '#f8f9fa', color: '#333',
  },
  convertBtn: {
    padding: '6px 12px', borderRadius: 8, border: 'none',
    background: '#e91e63', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600,
  },
  timestamp: { fontSize: 11, color: '#9aa0a6', marginTop: 8 },
  logToggle: {
    marginTop: 10, background: 'none', border: 'none', color: '#e91e63',
    fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
  },
  logBox: {
    marginTop: 10, background: '#f8f9fa', borderRadius: 10, padding: 12,
    border: '1px solid #e3e5e8',
  },
  logAdd: { display: 'flex', gap: 8, marginBottom: 10 },
  logTextarea: {
    flex: 1, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e3e5e8',
    fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: '#fff',
  },
  logSaveBtn: {
    padding: '0 14px', borderRadius: 8, border: 'none', background: '#e91e63',
    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  logEmpty: { fontSize: 12, color: '#aaa', textAlign: 'center', padding: 6 },
  logList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' },
  logEntry: { background: '#fff', borderRadius: 8, padding: '8px 10px', border: '1px solid #f5ebe4' },
  logEntryDate: { fontSize: 10.5, color: '#9aa0a6', marginBottom: 3, fontWeight: 600 },
  logEntryText: { fontSize: 13, color: '#444', lineHeight: 1.4 },
}
